import * as React from 'react';
import { connect } from 'react-redux';
import * as moment from 'moment';
import { useHistory } from 'react-router-dom';

import ButtonForm from '../../modules/ButtonForm/ButtonForm';
import ErrorMessage from '../../modules/ErrorMessage/ErrorMessage';
import FieldsetForm from '../../components/FieldsetForm/FieldsetForm';
import Preloader from '../../components/PreLoader/PreLoader';

import * as FindUserActions from '../../actions/findUser';
import * as AuthCreators from '../../creators/auth';
import * as Const from '../../constants';
import * as FindUserSelector from '../../selectors/findUser';
import { currentProductId$ } from '../../selectors/product';
import {
  dateEnd$,
  dateStart$
} from '../../selectors/datepickers';

import State from '../../interfaces/states/State';

import './style.scss';

interface DispatchProps {
  authIdValue: (value: string) => void;
  getEmailValue: (value: string) => void;
  findUserInit: () => void;
  activeTabAction: (value: number) => void;
  searchErrorInit: () => void;
  fetchRefreshAuth: (firstLoad: boolean) => void;
}

interface OwnProps {
  authIdInputValue: string;
  emailInputValue: string;
  disabled: boolean;
  isError: boolean;
  isLoading: boolean;
  isErrorWhileSearch: boolean;
  currentProductId: number;
  activeTab: number;
  dateStart: moment.Moment;
  dateEnd: moment.Moment;
}

interface Props extends DispatchProps, OwnProps {}

const Form = (props: Props) => {
  const history = useHistory();
  const [idField, setIdField] = React.useState(Const.EMPTY_FIELD);
  const [emailField, setEmailField] = React.useState(Const.EMPTY_FIELD);

  const {
    getEmailValue,
    authIdValue,
    isError,
    fetchRefreshAuth,
    searchErrorInit,
    isErrorWhileSearch,
    isLoading,
    activeTabAction,
  } = props;

  const onAuthIdFindVal = React.useCallback((e) => setIdField(e.target.value.replace(/\D/g, '')), [setIdField]);

  const onEmailFindVal = React.useCallback((e) => setEmailField(e.target.value), [setEmailField]);

  const clearId = () => {
    setIdField(Const.EMPTY_FIELD);
    authIdValue(Const.EMPTY_FIELD);
  };

  const clearEmail = () => {
    setEmailField(Const.EMPTY_FIELD);
    getEmailValue(Const.EMPTY_FIELD);
  };

  const findUserButtonClick = e => {
    e.preventDefault();

    if (idField) {
      authIdValue(idField);
      history.push({
        pathname: '/',
        search: `?id=${idField}`,
      });
    } else if (emailField) {
      getEmailValue(emailField);
      history.push({
        pathname: '/',
        search: `?email=${emailField}`,
      });
    }

    if (idField || emailField) {
      const isFirstPage = false;
      fetchRefreshAuth(isFirstPage);
      if (!isError) {
        return setTimeout(() => activeTabAction(Const.PERSONAL_ACTIVE_TAB), 3000);
      }
    } else {
      alert('You must fill at least one field!');
    }
  };

  const errorMessage = () => setTimeout(() => searchErrorInit(), 5000);

  return(

    <form className='d-flex flex-column align-center justify-center firstpage-form'>
      <span className='form-header'>{'Find user'}</span>
      <div className='input-container d-flex flex-column'>

        <FieldsetForm
          onChange={onAuthIdFindVal}
          onFocus={clearEmail}
          error={isErrorWhileSearch}
          placeholder='auth_id'
          type='text'
          disabled={isLoading}
          fieldsetValue={idField} />

        <FieldsetForm
          onChange={onEmailFindVal}
          onFocus={clearId}
          error={isErrorWhileSearch}
          placeholder='email'
          type='email'
          disabled={isLoading}
          fieldsetValue={emailField} />
      </div>

      {
        isLoading ?
          <Preloader /> :
          <ButtonForm
            label={'Search'}
            onButtonClick={findUserButtonClick} />
      }

      { !!isErrorWhileSearch && <ErrorMessage message='User is not found!' /> }

      { !!isErrorWhileSearch && errorMessage() }
    </form>
  );
};

const mapStateToProps = (state: State) => {
  return {
    dateEnd: dateEnd$(state),
    dateStart: dateStart$(state),
    activeTab: FindUserSelector.activeTab$(state),
    currentProductId: currentProductId$(state),
    authIdInputValue: FindUserSelector.authIdInputValue$(state),
    emailInputValue: FindUserSelector.emailInputValue$(state),
    disabled: FindUserSelector.isDisabled$(state),
    isError: FindUserSelector.isError$(state),
    isLoading: FindUserSelector.isFindUserLoading$(state),
    isErrorWhileSearch: FindUserSelector.isErrorWhileSearch$(state),
  };
};

const mergeProps = (stateProps, dispatchProps: DispatchProps, props: OwnProps): Props => {
  return {
    ...stateProps,
    authIdValue: dispatchProps.authIdValue,
    getEmailValue: dispatchProps.getEmailValue,
    findUserInit: dispatchProps.findUserInit,
    activeTabAction: dispatchProps.activeTabAction,
    searchErrorInit: dispatchProps.searchErrorInit,
    fetchRefreshAuth: dispatchProps.fetchRefreshAuth,
    ...props,
  };
};

export default connect(
  mapStateToProps, {
    authIdValue: FindUserActions.fetchFindUserAuthIdAction,
    getEmailValue: FindUserActions.fetchFindUserEmailAction,
    findUserInit: FindUserActions.fetchFindUserInitAction,
    activeTabAction: FindUserActions.fetchActiveTabAction,
    searchErrorInit: FindUserActions.fetchFindUserSearchErrorInit,
    fetchRefreshAuth: AuthCreators.fetchRefreshAuth,
  }, mergeProps
)(React.memo(Form));
